import type { NetworkExitNode } from "../../../../db/schema";
import { isExitNodeConnected } from "../../../../shared/tunnel/health";
import { useNow } from "../../../hooks/useNow";
import { formatRelativeTime } from "../../../lib/formatRelativeTime";
import { StatusBadge } from "../../StatusBadge";
import { Text } from "../../Text";
import {
  Card,
  DetailList,
  DetailRow,
  NeutralIconBadge,
  RowNameContent,
  RowNameLine,
  SectionHeading,
} from "../shared";

type ExitNode = Pick<
  NetworkExitNode,
  "id" | "peerAddress" | "connectedAt" | "lastSeenAt"
>;

type ConnectionState = {
  disabledAt: number | null;
  exitNodes: readonly ExitNode[];
};

function liveExitNodes(
  exitNodes: readonly ExitNode[],
  now: number
): ExitNode[] {
  return exitNodes
    .filter((node) => isExitNodeConnected(node.lastSeenAt, now))
    .sort((a, b) => a.connectedAt - b.connectedAt);
}

export function NetworkConnectionStatus({
  network,
}: {
  network: ConnectionState;
}) {
  const now = useNow(5_000);
  if (network.disabledAt != null)
    return <StatusBadge color="gray">Disabled</StatusBadge>;
  const live = liveExitNodes(network.exitNodes, now);
  if (live.length === 0)
    return <StatusBadge color="orange">Not connected</StatusBadge>;
  return <StatusBadge color="green">{live.length} connected</StatusBadge>;
}

function exitNodeIp(peerAddress: string): string {
  return peerAddress.startsWith("[")
    ? peerAddress.slice(1, peerAddress.lastIndexOf("]"))
    : peerAddress.slice(0, peerAddress.lastIndexOf(":"));
}

export function NetworkConnectionPanel({
  network,
}: {
  network: ConnectionState;
}) {
  const now = useNow(5_000);
  const live = liveExitNodes(network.exitNodes, now);
  return (
    <Card>
      <SectionHeading>
        <RowNameLine>
          <Text variant="text-regular-md">Exit nodes</Text>
          <NetworkConnectionStatus network={network} />
        </RowNameLine>
        <Text variant="text-small" color="var(--text-secondary)">
          Nodes connected to this network. Updates automatically.
        </Text>
      </SectionHeading>
      {live.length === 0 ? (
        <SectionHeading>
          <Text variant="text-small" color="var(--text-secondary)">
            {network.disabledAt != null
              ? "Disabled networks don’t accept exit nodes."
              : "No exit nodes are connected."}
          </Text>
        </SectionHeading>
      ) : (
        <DetailList role="list" aria-label="Connected exit nodes">
          {live.map((node) => (
            <DetailRow key={node.id} role="listitem">
              <NeutralIconBadge iconName="laptop" />
              <RowNameContent>
                <Text variant="text-regular-md">
                  {exitNodeIp(node.peerAddress)}
                </Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  Connected {formatRelativeTime(node.connectedAt, now)}
                </Text>
              </RowNameContent>
            </DetailRow>
          ))}
        </DetailList>
      )}
    </Card>
  );
}
